<template>
  <div
    class="flex flex-col py-4"
    :class="variant === 'wide' ? 'md:py-10' : 'lg:py-10'"
  >
    <details
      ref="details"
      class="group peer"
      :class="
        variant === 'wide'
          ? 'open:max-md:mb-4 w-full'
          : 'open:max-lg:mb-0.5 md:w-1/2'
      "
      :aria-details="id"
      :open="defaultOpenPreview"
    >
      <summary class="list-none marker:hidden">
        <div
          class="flex w-full cursor-pointer gap-4"
          :class="
            variant === 'wide'
              ? 'max-md:justify-between md:h-min md:flex-row-reverse md:justify-end items-center'
              : 'max-lg:justify-between lg:h-min lg:flex-row-reverse lg:justify-end items-center'
          "
        >
          <h2>{{ $t('product_details.information.about_article') }}</h2>
          <IconPlus class="size-6 text-anthracite-800 group-open:hidden" />
          <IconMinus
            class="hidden size-6 text-anthracite-800 group-open:block"
          />
        </div>
      </summary>
    </details>
    <div
      :id="id"
      ref="content"
      class="overflow-hidden text-md transition-all"
      :class="variant === 'wide' ? 'w-full' : 'md:w-1/2'"
    >
      <div
        ref="inner"
        class="mt-2 md:mt-4"
        :class="hasInteracted ? 'flex flex-col' : 'line-clamp-4'"
        :style="dynamicHeight"
      >
        <template v-for="attr in about" :key="attr?.key">
          <dt class="mb-1 mt-3 text-lg font-medium">{{ attr?.label }}</dt>
          <template v-for="val in attr?.values" :key="val">
            <dd
              v-safe-html="sanitizeHtml(val)"
              class="text-lg font-normal"
            ></dd>
          </template>
        </template>
      </div>
      <SFButton
        v-if="!hasInteracted"
        variant="raw"
        class="mt-4"
        @click="handleShowMore"
      >
        {{ $t('product_detail_page.description.show_more') }}
      </SFButton>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue'
import { useEventListener } from '@vueuse/core'
import { sanitizeHtml } from '~/utils/sanitize'
import { SFButton } from '#storefront-ui/components'

type Attribute = null | {
  key: string
  label: string
  values: string[]
}

const { variant = 'wide', defaultOpenPreview = false } = defineProps<{
  title?: string
  variant?: 'narrow' | 'wide'
  id: string
  about: Attribute[]
  /** If true: details is open on mount (minus icon), but content shows only 3 lines until first interaction */
  defaultOpenPreview?: boolean
}>()

const open = ref(!!defaultOpenPreview)
// Important: even if we start open, user hasn’t interacted yet
const hasInteracted = ref(false)

const details = ref<HTMLDetailsElement>()
const content = ref<HTMLDivElement>()
const inner = ref<HTMLDivElement>() // the clamped container

// Keep state in sync with the native <details>
useEventListener(details, 'toggle', (event: ToggleEvent) => {
  const isOpen = event.newState === 'open'
  open.value = isOpen
  // If user closes the accordion at least once, next open should show full content
  if (!isOpen) {
    hasInteracted.value = true
  }
})

// Height animation states:
// - closed => 0px
// - open + !hasInteracted => previewHeight (3-lines)
// - open + hasInteracted => full scrollHeight
const dynamicHeight = computed(() => {
  if (!open.value) {
    return 'height: 0px'
  }
  if (open.value && !hasInteracted.value) {
    return ''
  }
  return 'height: 100%'
})

// “Show more” expands to full content without toggling <details>
const handleShowMore = () => {
  hasInteracted.value = true
}
</script>

<style scoped lang="css">
dd li {
  margin-left: 18px;
}
</style>
